import { useEffect, useRef } from 'react';

interface UseGenerationTimeoutOptions {
  itemIds: string[];
  timeoutMs: number;
  onTimeout: (id: string) => void;
}

export function useGenerationTimeout({
  itemIds,
  timeoutMs,
  onTimeout,
}: UseGenerationTimeoutOptions) {
  const callbackRef = useRef(onTimeout);
  callbackRef.current = onTimeout;

  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const currentTimers = timersRef.current;

    // Start timers for new items
    for (const id of itemIds) {
      if (!currentTimers.has(id)) {
        const timer = setTimeout(() => {
          currentTimers.delete(id);
          callbackRef.current(id);
        }, timeoutMs);
        currentTimers.set(id, timer);
      }
    }

    // Clear timers for items no longer generating
    for (const [id, timer] of currentTimers) {
      if (!itemIds.includes(id)) {
        clearTimeout(timer);
        currentTimers.delete(id);
      }
    }
  }, [itemIds, timeoutMs]);

  // Cleanup all timers on unmount
  useEffect(() => {
    const currentTimers = timersRef.current;
    return () => {
      currentTimers.forEach((timer) => clearTimeout(timer));
      currentTimers.clear();
    };
  }, []);
}
